<template>
  <div class="min-h-screen flex items-center justify-center p-6 bg-grid-pattern">
    <!-- Background effects -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/20 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -bottom-40 -left-40 w-80 h-80 bg-accent-red/20 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent-yellow/10 rounded-full blur-3xl"
      ></div>
    </div>

    <!-- Login card -->
    <div class="relative w-full max-w-md">
      <div class="card p-8">
        <!-- Logo -->
        <div class="text-center mb-8">
          <div
            class="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 via-accent-red to-accent-yellow mb-4 shadow-lg shadow-primary-500/25"
          >
            <span class="text-white font-bold text-3xl font-display">P</span>
          </div>
          <h1 class="font-display text-3xl font-bold text-gradient">pushx.pro</h1>
          <p class="text-gray-400 mt-2">Войдите в ваш аккаунт</p>
        </div>

        <!-- Error message -->
        <div
          v-if="errorMessage"
          class="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30"
        >
          <p class="text-sm text-red-400">{{ errorMessage }}</p>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleLogin" class="space-y-5">
          <div>
            <label class="form-label">Email</label>
            <input
              v-model="form.email"
              type="email"
              class="input-field"
              placeholder="email@example.com"
              required
              autocomplete="email"
            />
            <div v-if="errors.email" class="mt-1 text-sm text-red-400">
              {{ errors.email }}
            </div>
          </div>

          <div>
            <label class="form-label">Пароль</label>
            <div class="relative">
              <input
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                class="input-field pr-10"
                placeholder="••••••••"
                required
                autocomplete="current-password"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400 transition-colors"
              >
                <svg
                  v-if="showPassword"
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </button>
            </div>
            <div v-if="errors.password" class="mt-1 text-sm text-red-400">
              {{ errors.password }}
            </div>
          </div>

          <div class="flex justify-end">
            <button
              type="button"
              @click="$router.push('/forgot-password')"
              class="text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors duration-200"
            >
              Забыли пароль?
            </button>
          </div>

          <button
            type="submit"
            :disabled="loading"
            class="w-full btn-primary py-3 flex items-center justify-center gap-2 text-base font-semibold"
          >
            <svg
              v-if="loading"
              class="w-5 h-5 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ loading ? 'Вход...' : 'Войти' }}
          </button>
        </form>

        <!-- Footer -->
        <div class="mt-8 text-center">
          <p class="text-sm text-gray-500">
            Нет аккаунта?
            <button
              type="button"
              @click="$router.push('/register')"
              class="font-medium text-primary-500 hover:text-primary-400 transition-colors duration-200 ml-1"
            >
              Зарегистрироваться
            </button>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '../stores/auth';
import { useNotifications } from '../composables/useNotifications';

export default {
  name: 'Login',
  setup() {
    const authStore = useAuthStore();
    const { success, error } = useNotifications();

    return {
      authStore,
      success,
      error,
    };
  },
  data() {
    return {
      form: {
        email: '',
        password: '',
      },
      showPassword: false,
      errors: {},
      loading: false,
      errorMessage: '',
    };
  },
  methods: {
    validateForm() {
      this.errors = {};

      if (!this.form.email) {
        this.errors.email = 'Введите email';
        return false;
      }

      if (!this.isValidEmail(this.form.email)) {
        this.errors.email = 'Введите корректный email';
        return false;
      }

      if (!this.form.password) {
        this.errors.password = 'Введите пароль';
        return false;
      }

      if (this.form.password.length < 8) {
        this.errors.password = 'Пароль должен содержать минимум 8 символов';
        return false;
      }

      return true;
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    async handleLogin() {
      this.errorMessage = '';
      
      if (!this.validateForm()) {
        return;
      }

      try {
        this.loading = true;

        await this.authStore.login(this.form);

        this.success('Успех', 'Успешный вход!');

        await this.$nextTick();
        this.$router.push('/dashboard');
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Ошибка входа';
        this.error('Ошибка', this.errorMessage);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
